"use client"

import { Button } from '@/shared/components/ui/button'
import { DownloadCloud } from 'lucide-react'
import React, { useState, useCallback } from 'react'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'
import { useVolunteersListQueryParams } from '../hooks/volunteers.hooks'
import { getVolunteerTableRows } from '@/features/volunteers/api/volunteers.api'
import { toast } from 'sonner'
import type { VolunteerTableRow } from '../types/volunteers.type'
import { format } from 'date-fns'

function escapeCsvCell(val: string | number | null | undefined): string {
    if (val === null || val === undefined) return ''
    const s = String(val).trim()
    if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`
    return s
}

function servePeriodDateToStr(val: unknown): string {
    if (typeof val === 'string') return val
    if (val instanceof Date) return format(val, 'yyyy-MM-dd')
    return ''
}

function getServePeriodDisplay(row: VolunteerTableRow): { arrival: string; departure: string } {
    const dates = row.volunteer?.serveDates
    if (!dates?.length) return { arrival: '', departure: '' }
    const first = dates[0]
    const from = first?.from
    const to = first?.to
    if (!from && !to) return { arrival: '', departure: '' }
    try {
        const fromStr = servePeriodDateToStr(from)
        const toStr = servePeriodDateToStr(to)
        return { arrival: fromStr, departure: toStr }
    } catch {
        return { arrival: '', departure: '' }
    }
}

function VolunteerDownloadButton() {
    const { session } = useSession()
    const params = useVolunteersListQueryParams()
    const [isLoading, setIsLoading] = useState(false)
    const canDownload = session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER

    const handleDownload = useCallback(async () => {
        if (!canDownload) return
        setIsLoading(true)
        try {
            const pageSize = 100
            const allUsers: VolunteerTableRow[] = []
            let page = 1
            let hasMore = true

            while (hasMore) {
                const result = await getVolunteerTableRows({
                    ...params,
                    page,
                    limit: pageSize,
                    detail: 'full',
                })
                if (!result.success || !result.data?.users) {
                    toast.error(!result.success ? result.message : 'Failed to load volunteers for download')
                    return
                }
                const users = result.data.users as VolunteerTableRow[]
                allUsers.push(...users)
                hasMore = users.length >= pageSize
                page += 1
            }

            const headers = ['Full Name', 'Email', 'Phone', 'Organization', 'Role', 'Status', 'Arrival Date', 'Departure Date', 'Last Active']
            const rows = allUsers.map((row) => {
                const { arrival, departure } = getServePeriodDisplay(row)
                const status = (row.volunteer as { status?: boolean } | undefined)?.status === false ? 'Inactive' : 'Active'
                const roleName = row.role?.name ?? ''
                const lastActive = row.lastActiveAt
                    ? format(new Date(row.lastActiveAt), 'yyyy-MM-dd')
                    : ''
                return [
                    escapeCsvCell(row.fullname),
                    escapeCsvCell(row.email),
                    escapeCsvCell(row.phone),
                    escapeCsvCell(row.organizationName ?? row.volunteer?.organizationName),
                    escapeCsvCell(roleName),
                    escapeCsvCell(status),
                    escapeCsvCell(arrival),
                    escapeCsvCell(departure),
                    escapeCsvCell(lastActive),
                ].join(',')
            })
            const csv = [headers.join(','), ...rows].join('\n')
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `volunteers-${format(new Date(), 'yyyy-MM-dd')}.csv`
            a.click()
            URL.revokeObjectURL(url)
            toast.success(`Downloaded ${allUsers.length} volunteer${allUsers.length !== 1 ? 's' : ''}`)
        } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Download failed')
        } finally {
            setIsLoading(false)
        }
    }, [canDownload, params])

    if (!canDownload) {
        return null
    }

    return (
        <Button
            type="button"
            variant={"default"}
            className='bg-primary hover:bg-primary/90 text-white font-bold rounded-md gap-2 py-1 h-10 flex-shrink-0'
            size={"sm"}
            onClick={handleDownload}
            disabled={isLoading}
        >
            <DownloadCloud className="w-5 h-5" />
            <span className="hidden sm:inline">{isLoading ? 'Downloading…' : 'Download'}</span>
        </Button>
    )
}

export default VolunteerDownloadButton